/**
 * Challenge Profile Screen — full-screen slide-in with hero, progress,
 * action buttons (5 states), 4 tabs (Tasks, Leaderboard, Info, Reviews).
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Share2, LogOut, UserPlus, Trophy,
  ListChecks, Medal, MessageCircle, Info, Lock,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

import { useAuthStore } from '@turnifly/shared/stores'
import { Button } from '@turnifly/shared/ui'
import { Skeleton } from '@turnifly/shared/ui'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { getChallengeShareLink } from '@turnifly/shared/api'
import {
  useChallengeProfile,
  useJoinChallenge,
  useLeaveChallenge,
  useUserChallenges,
} from '@turnifly/shared/hooks'

import { ChallengeCoverSection } from './challenge-cover-section'
import { ChallengeProgressSection } from './challenge-progress-section'
import { ChallengeTasksTab } from './challenge-tasks-tab'
import { ChallengeLeaderboardTab } from './challenge-leaderboard-tab'
import { ChallengeInfoTab } from './challenge-info-tab'
import { ChallengeReviewsTab } from './challenge-reviews-tab'
import { HabitFormSheet } from '../../home/components/habit-form/habit-form-sheet'

// ── Types ────────────────────────────────────────────────────

type ChallengeTab = 'tasks' | 'members' | 'info' | 'reviews'

interface TabDef {
  id: ChallengeTab
  label: string
  icon: React.FC<{ size: number; className?: string }>
}

// ── Tab pills ────────────────────────────────────────────────

function ChallengeTabPills({ tabs, active, onChange }: {
  tabs: TabDef[]
  active: ChallengeTab
  onChange: (tab: ChallengeTab) => void
}): React.ReactElement {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-white/[0.06] bg-white/[0.04] p-1 backdrop-blur-xl">
      {tabs.map((tab) => {
        const isActive = tab.id === active
        const Icon = tab.icon
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => { onChange(tab.id); hapticImpact('light') }}
            className={`relative whitespace-nowrap rounded-full px-4 py-2 text-[12px] font-semibold transition-colors tap-highlight-transparent ${
              isActive ? 'text-white' : 'text-[var(--color-text-hint)]/50 hover:text-[var(--color-text-hint)]'
            }`}
          >
            {isActive && (
              <motion.div
                layoutId="challengeProfileTabPill"
                className="absolute inset-0 rounded-full border border-white/[0.06] bg-white/[0.08]"
                transition={{ type: 'spring', stiffness: 350, damping: 28 }}
              />
            )}
            <span className="relative z-10 flex items-center justify-center gap-1.5">
              <Icon size={13} />
              {tab.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ── Action button section ────────────────────────────────────

function ActionSection({ profile, user, id, t, navigate }: {
  profile: NonNullable<ReturnType<typeof useChallengeProfile>['data']>
  user: { id: string; is_premium?: boolean } | null
  id: string
  t: (key: string, opts?: Record<string, unknown>) => string
  navigate: ReturnType<typeof useNavigate>
}): React.ReactElement | null {
  const joinMutation = useJoinChallenge()
  const leaveMutation = useLeaveChallenge()
  const { data: userChallenges } = useUserChallenges(user?.id)

  const { challenge, membership } = profile
  const isPremium = user?.is_premium ?? false
  const isCreator = user ? challenge.creator_id === user.id : false
  const isMember = !!membership && membership.status !== 'left'
  const isCompleted = membership?.status === 'completed'
  const isActive = challenge.status === 'active'
  const isUpcoming = challenge.status === 'upcoming'
  const canJoin = !isMember && (isActive || isUpcoming)

  // Free user: max 1 active challenge
  const activeCount = userChallenges?.filter((c) => c.status === 'active').length ?? 0
  const isFreeLimitReached = !isPremium && activeCount >= 1

  // Check join policy
  const isJoinClosed = (() => {
    if (!isActive) return false
    if (challenge.join_policy === 'before_start') return true
    if (challenge.join_policy === 'first_3_days' && challenge.start_date) {
      const start = new Date(challenge.start_date)
      const grace = new Date(start.getTime() + 3 * 86400000)
      return new Date() > grace
    }
    return false
  })()

  const handleJoin = (): void => {
    if (!user) return

    // Paid challenge → redirect to payment
    if (challenge.is_paid && challenge.price) {
      navigate(`/profile/premium`)
      return
    }

    // Free user limit
    if (isFreeLimitReached) {
      hapticNotification('warning')
      return
    }

    joinMutation.mutate({
      challengeId: id,
      userId: user.id,
      startDate: challenge.start_date,
      endDate: challenge.end_date,
    })
    hapticImpact('medium')
  }

  const handleLeave = (): void => {
    if (!user) return
    leaveMutation.mutate({ challengeId: id, userId: user.id })
    hapticImpact('medium')
  }

  return (
    <div className="px-4 py-2">
      {/* Free user limit reached */}
      {canJoin && !isJoinClosed && isFreeLimitReached && (
        <div className="flex flex-col items-center gap-2 rounded-xl bg-white/[0.04] px-4 py-3">
          <p className="text-[13px] text-text-hint text-center">{t('challenges.profile.freeLimitReached')}</p>
          <Button variant="primary" size="sm" onClick={() => navigate('/profile/premium')}>
            {t('premium.upgrade')}
          </Button>
        </div>
      )}

      {/* Case 1: Can join, not closed */}
      {canJoin && !isJoinClosed && !isFreeLimitReached && (
        <Button
          variant="primary"
          fullWidth
          isLoading={joinMutation.isPending}
          onClick={handleJoin}
        >
          <UserPlus size={16} className="mr-2" />
          {challenge.is_paid && challenge.price
            ? t('challenges.profile.joinPaid', { price: challenge.price.toLocaleString() })
            : t('challenges.profile.join')}
        </Button>
      )}

      {/* Case 2: Join closed */}
      {canJoin && isJoinClosed && (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-white/[0.04] px-4 py-3">
          <Lock size={14} className="text-[var(--color-text-hint)]" />
          <span className="text-[13px] font-medium text-[var(--color-text-hint)]">
            {t('challenges.profile.policyBeforeStart')}
          </span>
        </div>
      )}

      {/* Case 3: Active member */}
      {isMember && !isCompleted && !isCreator && (
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-xl bg-white/[0.04] px-4 py-2.5 text-center">
            <p className="text-[13px] font-semibold text-white">
              {t('challenges.profile.participating')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleLeave}
            disabled={leaveMutation.isPending}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-danger)]/10 text-[var(--color-danger)] tap-highlight-transparent"
          >
            <LogOut size={16} />
          </button>
        </div>
      )}

      {/* Case 4: Completed */}
      {isCompleted && (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/10 px-4 py-3">
          <Trophy size={16} className="text-[var(--color-success)]" />
          <span className="text-[13px] font-semibold text-[var(--color-success)]">
            {t('challenges.profile.finished')}
          </span>
        </div>
      )}

      {/* Case 5: Creator */}
      {isCreator && !isCompleted && (
        <div className="rounded-xl bg-white/[0.04] px-4 py-3 text-center">
          <p className="text-[13px] font-semibold text-[var(--color-text-hint)]">
            {t('challenges.profile.youAreCreator')}
          </p>
        </div>
      )}
    </div>
  )
}

// ── Main Component ───────────────────────────────────────────

export function ChallengeProfileScreen(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)

  const { data: profile, isLoading } = useChallengeProfile(id)

  const [activeTab, setActiveTab] = useState<ChallengeTab>('tasks')
  const [isTabsSticky, setTabsSticky] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef(0)

  const tabs = useMemo<TabDef[]>(
    () => [
      { id: 'tasks', label: t('challenges.profile.tasks'), icon: ListChecks },
      { id: 'members', label: t('challenges.profile.leaderboard'), icon: Medal },
      { id: 'info', label: t('challenges.profile.info'), icon: Info },
      { id: 'reviews', label: t('challenges.profile.reviews'), icon: MessageCircle },
    ],
    [t],
  )

  // Sticky tabs (rAF throttled)
  const handleScroll = useCallback((): void => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      if (tabsRef.current && scrollRef.current) {
        const offset = tabsRef.current.offsetTop - 56
        setTabsSticky(scrollRef.current.scrollTop >= offset)
      }
      rafRef.current = 0
    })
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', handleScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [handleScroll])

  // Derived
  const challenge = profile?.challenge
  const membership = profile?.membership
  const isCreator = challenge && user ? challenge.creator_id === user.id : false
  const isMember = !!membership && membership.status !== 'left'
  const isActive = challenge?.status === 'active'
  const canReview = membership?.status === 'completed'

  const handleShare = (): void => {
    if (!challenge?.short_code) return
    const link = getChallengeShareLink(challenge.short_code)
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(`${challenge.name} — Turnifly!`)}`
    window.open(shareUrl, '_blank', 'noopener')
    hapticImpact('light')
  }

  // Loading
  if (isLoading || !profile || !challenge) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-[var(--color-bg)]">
        <div className="w-full max-w-lg space-y-3 px-4">
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="mx-auto h-10 w-2/3 rounded-full" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </div>
    )
  }

  return (
    <AnimatePresence>
      <motion.div
        key="challenge-profile"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 220 }}
        className="fixed inset-0 z-50 bg-[var(--color-bg)]"
      >
        {/* Fixed header */}
        <div className="fixed left-0 right-0 top-0 z-10 border-b border-white/[0.06] bg-[var(--color-bg)]/90 backdrop-blur-sm tg-header-safe-area">
          <div className="mx-auto flex max-w-lg items-center gap-3 px-4 py-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="-ml-2 rounded-xl p-2 transition-colors hover:bg-white/[0.04] tap-highlight-transparent"
            >
              <ArrowLeft size={20} className="text-white" />
            </button>
            <span className="flex-1 truncate text-[15px] font-bold text-white">
              {challenge.name}
            </span>
            <button
              type="button"
              onClick={handleShare}
              className="-mr-2 rounded-xl p-2 transition-colors hover:bg-white/[0.04] tap-highlight-transparent"
            >
              <Share2 size={18} className="text-[var(--color-text-hint)]" />
            </button>
          </div>
        </div>

        {/* Sticky tabs (appears on scroll) */}
        {isTabsSticky && (
          <div className="fixed left-0 right-0 z-10 border-b border-white/[0.04] bg-[var(--color-bg)]/90 backdrop-blur-xl" style={{ top: 'calc(52px + var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px))' }}>
            <div className="mx-auto flex max-w-lg justify-center py-2">
              <ChallengeTabPills tabs={tabs} active={activeTab} onChange={setActiveTab} />
            </div>
          </div>
        )}

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto overscroll-y-contain pt-14 tg-header-safe-area"
        >
          <div className="mx-auto max-w-lg pb-24 pt-2">
            {/* Hero */}
            <ChallengeCoverSection
              challenge={challenge}
              creator={profile.creator}
              membersPreview={profile.members_preview}
              habitsCount={profile.habits.length}
              isCreator={isCreator}
            />

            {/* Progress ring (member + active only) */}
            {isMember && isActive && membership && (
              <ChallengeProgressSection
                membership={membership}
                challenge={challenge}
                habitProgress={profile.user_habit_progress}
              />
            )}

            {/* Action buttons */}
            <ActionSection profile={profile} user={user} id={id!} t={t} navigate={navigate} />

            {/* Tab anchor */}
            <div ref={tabsRef} className="flex justify-center px-4 py-3">
              <ChallengeTabPills tabs={tabs} active={activeTab} onChange={setActiveTab} />
            </div>

            {/* Tab content */}
            {activeTab === 'tasks' && (
              <ChallengeTasksTab
                challengeId={id!}
                habits={profile.habits}
                userProgress={profile.user_habit_progress}
                isMember={isMember}
                isCreator={isCreator}
              />
            )}
            {activeTab === 'members' && (
              <ChallengeLeaderboardTab
                challengeId={id!}
                top3={profile.leaderboard_top3}
                userId={user?.id}
              />
            )}
            {activeTab === 'info' && (
              <ChallengeInfoTab
                challenge={challenge}
                prizes={profile.prizes}
                sponsors={profile.sponsors}
              />
            )}
            {activeTab === 'reviews' && (
              <ChallengeReviewsTab
                challengeId={id!}
                reviewsSummary={profile.reviews_summary}
                userReview={profile.user_review}
                canReview={canReview}
              />
            )}
          </div>
        </div>

        {/* Habit form for adding challenge habits */}
        {isCreator && <HabitFormSheet />}
      </motion.div>
    </AnimatePresence>
  )
}
